<template>
  <WdsAlert v-if="error" error data-test="compliance-review-hires-error">
    {{ $t('The hires could not be loaded. Try refreshing the page.') }}
  </WdsAlert>

  <div
    v-else-if="loading && !result"
    role="status"
    :aria-label="$t('Loading hires')"
    data-test="compliance-review-hires-loading"
  >
    <LoadingList :count="3" no-bg />
  </div>

  <WdsStack
    v-else-if="hasHires"
    direction-column
    gap-md
    data-test="compliance-review-hires"
  >
    <WdsStack align-center gap-sm>
      <WdsHeading type="title3">{{ $t('Hires') }}</WdsHeading>
      <WdsLabel
        sm
        gray
        subtle
        :label="`${currentHires.length}`"
        data-test="compliance-review-hires-count"
      />
    </WdsStack>

    <WdsText v-if="currentHires.length === 0" sm secondary data-test="compliance-review-hires-none-current">
      {{ $t('No current hires') }}
    </WdsText>

    <ComplianceHireCard
      v-for="hire in currentHires"
      :key="hire.id"
      :hire="hire"
      @select="emit('select', hire.id)"
    />

    <WdsCollapse
      v-if="pastHires.length > 0"
      :open="pastOpen"
      content-id="compliance-review-hires-past"
      class="tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 tw-pt-1"
      @update:open="pastToggled = $event"
    >
      <template #title>
        <div
          class="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-py-2.5"
          data-test="compliance-review-hires-past-toggle"
        >
          <WdsText class="tw-font-semibold">{{ $t('Ended') }}</WdsText>
          <WdsLabel sm gray subtle :label="`${pastHires.length}`" />
          <span class="tw-flex-1" />
          <WdsIcon :name="pastOpen ? 'chevron-up' : 'chevron-down'" :size="16" />
        </div>
      </template>

      <template #content>
        <WdsStack direction-column gap-md class="tw-pt-md">
          <ComplianceHireCard
            v-for="hire in pastHires"
            :key="hire.id"
            :hire="hire"
            @select="emit('select', hire.id)"
          />
        </WdsStack>
      </template>
    </WdsCollapse>
  </WdsStack>

  <WdsStack
    v-else
    direction-column
    align-center
    justify-center
    gap-sm
    class="tw-px-4 tw-py-10 tw-text-center"
    data-test="compliance-review-hires-empty"
  >
    <WdsIcon name="hire" :size="32" color="#9999AA" aria-hidden="true" data-test="compliance-review-hires-empty-icon" />
    <WdsText class="tw-font-medium">{{ $t('No hires yet') }}</WdsText>
    <WdsText sm secondary>{{ emptyMessage }}</WdsText>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useDocumentVisibility } from '@vueuse/core'
import {
  WdsAlert,
  WdsCollapse,
  WdsHeading,
  WdsIcon,
  WdsLabel,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import ComplianceHireCard from '@/components/compliances/ComplianceReviewModal/ComplianceHireCard.vue'
import { ComplianceReviewHireFragment } from '@/components/compliances/ComplianceReviewModal/fragments'
import { graphql, useFragment } from '@/gql'
import { HireActiveStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { isWorkerNamePlaceholder } from '@/utils/worker'

const props = defineProps<{
  trustedContactId: string
}>()

const emit = defineEmits<{ select: [hireId: string]; count: [count: number] }>()

// Drafts only come back when asked for, so they are fetched next to the rest and joined here.
const { result, loading, error, refetch } = useQuery(
  graphql(`
    query ComplianceReviewHires($id: ID!, $companies: [ID!]) {
      trustedContact(id: $id) {
        id
        worker {
          id
          name
          hires(companies: $companies, first: 50) {
            data {
              id
              ...ComplianceReviewHire
            }
          }
          drafts: hires(companies: $companies, status: DRAFT, first: 50) {
            data {
              id
              ...ComplianceReviewHire
            }
          }
        }
      }
    }
  `),
  () => ({ id: props.trustedContactId, companies: [currentAccount.globalId] }),
  // Checks are confirmed on the hire in another tab, so the list is fetched fresh on every opening.
  { clientId: 'publicApi', fetchPolicy: 'cache-and-network' },
)

// Fetch the list again when the reviewer comes back from confirming checks in another tab.
watch(useDocumentVisibility(), (visibility) => {
  if (visibility === 'visible') {
    refetch()
  }
})

// With nothing current to show, the ended hires are all there is, so they start open.
const pastToggled = ref<boolean | null>(null)

const PAST_STATUSES = [
  HireActiveStatus.Ended,
  HireActiveStatus.Terminated,
  HireActiveStatus.Cancelled,
]

const hires = computed(() => {
  const worker = result.value?.trustedContact?.worker

  return [...(worker?.drafts.data ?? []), ...(worker?.hires.data ?? [])]
})

const isPast = (hire: (typeof hires.value)[number]) =>
  PAST_STATUSES.includes(useFragment(ComplianceReviewHireFragment, hire).activeStatus)

const currentHires = computed(() => hires.value.filter((hire) => !isPast(hire)))
const pastHires = computed(() => hires.value.filter(isPast))
const hasHires = computed(() => hires.value.length > 0)

const pastOpen = computed(() => pastToggled.value ?? currentHires.value.length === 0)

watch(
  () => (result.value ? hires.value.length : null),
  (count) => {
    if (count !== null) {
      emit('count', count)
    }
  },
  { immediate: true },
)

const emptyMessage = computed(() => {
  const name = result.value?.trustedContact?.worker?.name
  const firstName = isWorkerNamePlaceholder(name) ? undefined : name?.split(' ')[0]

  return firstName
    ? $t(':name’s hires will show here.', { name: firstName })
    : $t('This worker’s hires will show here.')
})
</script>
